import { Link } from 'react-router-dom';
import { EyeOff } from 'lucide-react';

import { WAYPOINT_TYPE_LABELS, WAYPOINT_TYPE_COLORS } from '@/components/map/map-utils';
import type { Waypoint, BlindMetadata, FoodSourceMetadata, CameraMetadata } from '@/types/waypoint';

interface WaypointItemProps {
  waypoint: Waypoint;
  propertyId: string;
}

/**
 * Returns glance-metadata string for a waypoint — type label + key type-specific attribute.
 * Format: "TYPE · key attribute · best time"
 */
function getGlanceMetadata(waypoint: Waypoint): string {
  const typeLabel = WAYPOINT_TYPE_LABELS[waypoint.type].toUpperCase();
  const { metadata } = waypoint;
  if (!metadata) return typeLabel;

  switch (metadata.type) {
    case 'blind': {
      const m = metadata as BlindMetadata;
      const parts: string[] = [typeLabel];
      if (m.idealWindDirections?.length) {
        parts.push(`${m.idealWindDirections.slice(0, 2).join('/')} Ideal`);
      }
      if (m.bestTimeOfDay) {
        const timeLabel =
          m.bestTimeOfDay === 'all_day' ? 'All Day'
          : m.bestTimeOfDay === 'morning' ? 'Morning'
          : 'Evening';
        parts.push(timeLabel);
      }
      return parts.join(' · ');
    }
    case 'food_source': {
      const m = metadata as FoodSourceMetadata;
      const parts: string[] = [typeLabel];
      if ('plantedSpecies' in m && m.plantedSpecies?.length) {
        parts.push(m.plantedSpecies[0].replace(/_/g, ' '));
      } else if ('mineralType' in m && m.mineralType) {
        parts.push(m.mineralType);
      } else if ('foodSourceType' in m) {
        parts.push(m.foodSourceType.replace(/_/g, ' '));
      }
      if ('plotSizeAcresCalculated' in m && m.plotSizeAcresCalculated) {
        parts.push(`${m.plotSizeAcresCalculated.toFixed(1)} ac`);
      } else if ('plotSizeAcres' in m && (m as Record<string, unknown>).plotSizeAcres) {
        // CAST: accessing optional plotSizeAcres field dynamically
        const acres = (m as Record<string, unknown>).plotSizeAcres as number;
        parts.push(`${acres} ac`);
      }
      return parts.join(' · ');
    }
    case 'camera': {
      const m = metadata as CameraMetadata;
      const parts: string[] = [typeLabel];
      if (m.cloudPlatform && m.cloudPlatform !== 'none') {
        // Capitalize first letter
        parts.push(m.cloudPlatform.charAt(0).toUpperCase() + m.cloudPlatform.slice(1));
      } else if (m.cameraModel) {
        parts.push(m.cameraModel);
      } else {
        parts.push(m.cameraType === 'cellular' ? 'Cellular' : 'SD Card');
      }
      return parts.join(' · ');
    }
    case 'water_source': {
      const parts: string[] = [typeLabel];
      if (metadata.waterType) parts.push(metadata.waterType.replace(/_/g, ' '));
      if (metadata.seasonal) parts.push('Seasonal');
      return parts.join(' · ');
    }
    case 'bedding_area': {
      const parts: string[] = [typeLabel];
      if (metadata.beddingType) parts.push(metadata.beddingType.replace(/_/g, ' '));
      if (metadata.coverDensity) parts.push(`${metadata.coverDensity} cover`);
      return parts.join(' · ');
    }
    default:
      return typeLabel;
  }
}

export function WaypointItem({ waypoint, propertyId }: WaypointItemProps) {
  const typeColor = WAYPOINT_TYPE_COLORS[waypoint.type];
  const glance = getGlanceMetadata(waypoint);

  return (
    <Link
      to={`/land/properties/${propertyId}/waypoints/${waypoint.id}`}
      className="flex items-stretch rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] shadow-card hover:bg-[var(--color-elevated)] hover:border-[var(--color-border-subtle)] transition-colors overflow-hidden"
      data-testid="waypoint-item"
    >
      {/* Type-colored left stripe */}
      <span
        className="w-0.5 flex-shrink-0 self-stretch"
        style={{ backgroundColor: typeColor }}
        data-stripe-color={typeColor}
        aria-hidden="true"
      />

      <div className="flex items-center justify-between p-3 flex-1 min-w-0">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-body-md font-medium text-[var(--color-text-primary)] truncate">
              {waypoint.name}
            </span>
            {waypoint.visibleOnMap === false && (
              <EyeOff size={12} className="text-[var(--color-text-placeholder)] flex-shrink-0" data-testid="hidden-indicator" />
            )}
          </div>
          {/* Glance metadata line — type label + key attributes */}
          <span
            className="text-body-xs text-[var(--color-text-placeholder)] truncate"
            data-testid="waypoint-glance-metadata"
          >
            {glance}
          </span>
        </div>
        <span className="text-[var(--color-text-placeholder)] ml-3 flex-shrink-0 text-body-xs">›</span>
      </div>
    </Link>
  );
}
